import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import {
  XMarkIcon,
  CloudArrowUpIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'
import {
  financeService,
  VaBalanceBulkResult,
} from '../../services/finance.service'

interface VaBalanceBulkUploadModalProps {
  open: boolean
  onClose: () => void
  onCompleted: () => void
}

const CSV_TEMPLATE_HEADERS = ['VA Number', 'VA Name', 'VA Balance']

const CSV_EXAMPLE_ROWS = [
  ['1234567890', 'Ahmad Bin Ismail', '1250.50'],
  ['9876543210', 'Siti Aminah', '0.00'],
]

// Same list the server accepts; browsers mislabel both formats freely, so the
// extension check is the real gate.
const OK_TYPES = [
  'text/csv',
  'text/plain',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '',
]

function downloadCsv(filename: string, rows: (string | number | null)[][]) {
  const escapeCell = (v: string | number | null) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const csv = rows.map((r) => r.map(escapeCell).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export default function VaBalanceBulkUploadModal({
  open,
  onClose,
  onCompleted,
}: VaBalanceBulkUploadModalProps) {
  const { t } = useTranslation()
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState<VaBalanceBulkResult | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  if (!open) return null

  const reset = () => {
    setFile(null)
    setResult(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  const handleClose = () => {
    if (uploading) return
    reset()
    onClose()
  }

  const downloadTemplate = () => {
    downloadCsv('va-balance-upload-template.csv', [
      CSV_TEMPLATE_HEADERS,
      ...CSV_EXAMPLE_ROWS,
    ])
  }

  const downloadErrors = () => {
    if (!result?.failed.length) return
    downloadCsv('va-balance-upload-errors.csv', [
      ['row', 'vaNumber', 'reasons'],
      ...result.failed.map((f) => [f.row, f.vaNumber ?? '', f.reasons.join('; ')]),
    ])
  }

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (f.size > 2 * 1024 * 1024) {
      toast.error(t('finance.bulkUpload.fileTooLarge', 'File must be smaller than 2 MB'))
      e.target.value = ''
      return
    }
    const name = f.name.toLowerCase()
    if (!OK_TYPES.includes(f.type) && !name.endsWith('.csv') && !name.endsWith('.xlsx')) {
      toast.error(t('finance.bulkUpload.invalidFile', 'Only CSV or XLSX files are allowed'))
      e.target.value = ''
      return
    }
    setFile(f)
    setResult(null)
  }

  const upload = async () => {
    if (!file) {
      toast.error(t('finance.bulkUpload.noFile', 'Please select a CSV or XLSX file first'))
      return
    }
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const data = await financeService.bulkUploadVaBalances(formData)
      setResult(data)
      if (data.failed.length === 0) {
        toast.success(
          t('finance.bulkUpload.resultSummary', {
            defaultValue: '{{updated}} of {{total}} rows updated',
            updated: data.updated,
            total: data.totalRows,
          }),
        )
      } else {
        toast.error(
          t('finance.bulkUpload.partialError', {
            defaultValue: '{{updated}} updated, {{failed}} failed',
            updated: data.updated,
            failed: data.failed.length,
          }),
        )
      }
      if (data.updated > 0) onCompleted()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="my-8 w-full max-w-2xl rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 p-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t('finance.bulkUpload.title', 'Bulk Upload VA Balances')}
            </h2>
            <p className="text-xs text-gray-500">
              {t(
                'finance.bulkUpload.subtitle',
                'Update many VA balances at once from a CSV or XLSX file (max 500 rows)',
              )}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            disabled={uploading}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 p-5">
          {/* Template download */}
          <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm text-gray-700 mb-3">
              {t(
                'finance.bulkUpload.templateHint',
                'Download the template, fill in one row per member, then upload it below. Columns: VA Number (6-20 digits), VA Name, VA Balance (RM, max 2 decimal places).',
              )}
            </p>
            <div className="mb-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
              {t(
                'finance.bulkUpload.nameCheckNote',
                'A row is only applied if the VA number exists and the VA name matches the account holder. Note: mismatched rows are reported, never applied.',
              )}
            </div>
            <button
              type="button"
              onClick={downloadTemplate}
              className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 hover:bg-gray-100"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {t('finance.bulkUpload.downloadTemplate', 'Download Template (CSV)')}
            </button>
          </div>

          {/* File picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('finance.bulkUpload.selectFile', 'Select CSV or XLSX file')}
            </label>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onPickFile}
              disabled={uploading}
              className="block w-full text-sm text-gray-500 border border-gray-300 rounded-lg px-3 py-2 file:mr-3 file:rounded-md file:border-0 file:bg-[#0D7330] file:px-3 file:py-1.5 file:text-white file:text-xs hover:file:bg-[#0a5c27] disabled:opacity-60"
            />
          </div>

          {/* Results */}
          {result && (
            <div className="space-y-3">
              <div
                className={`rounded-md px-4 py-3 text-sm ${
                  result.failed.length === 0
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : 'bg-amber-50 border border-amber-200 text-amber-800'
                }`}
              >
                {t('finance.bulkUpload.resultSummary', {
                  defaultValue: '{{updated}} of {{total}} rows updated',
                  updated: result.updated,
                  total: result.totalRows,
                })}
                {result.unchanged > 0 && (
                  <> — {t('finance.bulkUpload.unchangedNote', {
                    defaultValue: '{{count}} row(s) already had this balance',
                    count: result.unchanged,
                  })}</>
                )}
              </div>

              {result.failed.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-900">
                      {t('finance.bulkUpload.failedRows', 'Failed rows')}
                    </h3>
                    <button
                      type="button"
                      onClick={downloadErrors}
                      className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:underline"
                    >
                      <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                      {t('finance.bulkUpload.downloadErrors', 'Download error report (CSV)')}
                    </button>
                  </div>
                  <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            {t('finance.bulkUpload.row', 'Row')}
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            {t('finance.bulkUpload.vaNumber', 'VA Number')}
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            {t('finance.bulkUpload.reasons', 'Reasons')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {result.failed.map((f) => (
                          <tr key={`${f.row}-${f.vaNumber ?? ''}`}>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-500">{f.row}</td>
                            <td className="px-3 py-2 font-mono text-gray-900">{f.vaNumber || '—'}</td>
                            <td className="px-3 py-2 text-red-600">{f.reasons.join('; ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={uploading}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              {t('finance.bulkUpload.close', 'Close')}
            </button>
            <button
              type="button"
              onClick={upload}
              disabled={uploading || !file}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0D7330] px-5 py-2 text-sm font-medium text-white hover:bg-[#0a5c27] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <CloudArrowUpIcon className="h-4 w-4" />
              {uploading
                ? t('finance.bulkUpload.uploading', 'Uploading...')
                : t('finance.bulkUpload.upload', 'Upload')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
